import type { FileRecord } from "@/shared/domain/files";
import {
  type Axes,
  deriveSourceFileIdFromAxes,
} from "@/shared/state/visualization/schema/v1/three-d";

export interface AxesFileSelection {
  /**
   * The file_id to show as selected in the FileSelector, or `undefined` when
   * there is no resolvable selection.
   */
  fileId: string | undefined;

  /**
   * `true` when the component's bound axes reference a file not present in
   * the current workspace. Callers should surface a warning and require the
   * user to pick a file from the workspace to recover. `false` otherwise.
   */
  dangling: boolean;

  /**
   * The raw file_id candidate the resolver considered — derived from the
   * bound axes — regardless of whether that id is present in `workspaceFiles`.
   * Callers that surface dangling state should read this field rather than
   * re-deriving the candidate.
   */
  rawCandidate: string | undefined;
}

/**
 * Resolves which file a component's FileSelector should display by deriving
 * from the bound axes' TopicData. Bound axes within a single component
 * (Position, VectorField, ...) share a file, so any bound axis yields the
 * answer.
 *
 * `workspaceFiles` is the membership set used to test whether the bound file
 * is still in the workspace; pass the full workspace file list (not a
 * topic-filtered subset), or a binding may be misclassified as dangling
 * before topics finish loading.
 *
 * Explicitly never returns `workspaceFiles[0]` as a silent fallback: an
 * unresolvable pointer is surfaced via `dangling: true` so the UI can show
 * an error state, not a plausible-looking wrong file.
 */
export function resolveAxesFileSelection(
  axes: Axes | undefined,
  workspaceFiles: FileRecord[],
): AxesFileSelection {
  const candidate = deriveSourceFileIdFromAxes(axes);

  if (candidate === undefined) {
    return { fileId: undefined, dangling: false, rawCandidate: undefined };
  }

  const exists = workspaceFiles.some((file) => file.file_id === candidate);
  if (exists) {
    return { fileId: candidate, dangling: false, rawCandidate: candidate };
  }

  return { fileId: undefined, dangling: true, rawCandidate: candidate };
}
